import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

import { deleteShipmentTemplate } from "api";
import { useGetQuickRequestDetails } from "api/queries/quick-request/useGetQuickRequestDetails";
import { useGetQuoteRequestDetails } from "api/queries/quotes/useGetQuoteRequestDetails";
import { userQueryKeys } from "api/queries/user/userQueryKeys";
import testIds from "constants/testIds";
import errorToast from "helpers/errorToast";
import { IUserTemplate } from "types/Common";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import Alert from "components/molecules/Alert/Alert";
import CardNew from "components/molecules/Card/CardNew";
import Card from "components/molecules/Card/Card";
import QuickOrganisationalDetails from "pages/shipper/ShipmentDetails/components/QuickOrganisationalDetails";
import QuickSchedule from "pages/shipper/ShipmentDetails/components/QuickSchedule";
import QuoteAttachments from "pages/shipper/ShipmentDetails/components/QuoteAttachments";
import QuickService from "pages/shipper/ShipmentDetails/components/QuickService";
import QuickPackingList from "pages/shipper/ShipmentDetails/components/QuickPackingList";
import QuoteOrganisationalDetails from "pages/shipper/ShipmentDetails/components/QuoteOrganisationalDetails";
import QuoteSchedule from "pages/shipper/ShipmentDetails/components/QuoteSchedule";
import QuotePackingList from "pages/shipper/ShipmentDetails/components/QuotePackingList";
import QuoteService from "pages/shipper/ShipmentDetails/components/QuoteService";

interface ViewTemplateDialogProps {
  isOpen: boolean;
  close: () => void;
  selectedTemplate: IUserTemplate | undefined;
  templateType: "quote_request_templates" | "quick_request_templates";
}

const ViewTemplateDialog = function ({ isOpen, close, selectedTemplate, templateType }: ViewTemplateDialogProps) {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isQuickRequest = templateType === "quick_request_templates";

  const {
    data: quickDetails,
    isInitialLoading: quickDetailsLoading,
    refetch: refetchQuickDetails,
  } = useGetQuickRequestDetails(selectedTemplate?.reference_number ?? "", {
    enabled: false,
  });

  const {
    data: quoteDetails,
    isInitialLoading: quoteDetailsLoading,
    refetch: refetchQuoteDetails,
  } = useGetQuoteRequestDetails({
    shipmentId: selectedTemplate?.reference_number ?? "",
    options: {
      enabled: false,
    },
  });

  useEffect(() => {
    if (isQuickRequest) refetchQuickDetails();
    else refetchQuoteDetails();
  }, [selectedTemplate]);

  const data: any = isQuickRequest ? quickDetails : quoteDetails;
  const loadingData = isQuickRequest ? quickDetailsLoading : quoteDetailsLoading;

  const handleDeleteTemplate = () => {
    if (!selectedTemplate) return;
    setIsDeleting(true);

    const templateId = selectedTemplate.template_id;
    if (!templateId)
      errorToast(
        intl.formatMessage({
          id: "tasks.taskDialog.errorMessage",
          defaultMessage: "Something went wrong, try again in a few minutes.",
        }),
      );
    deleteShipmentTemplate(templateId)
      .then(() => {
        toast(
          intl.formatMessage({
            id: "components.molecules.navigation.deleteTemplateSuccess",
            defaultMessage: "Template deleted successfully",
          }),
          {
            type: "success",
          },
        );
        queryClient.invalidateQueries({ queryKey: userQueryKeys.userTemplates });
        setShowConfirmDelete(false);
      })
      .catch((err) => {
        errorToast(err);
      })
      .finally(() => {
        close();
        setIsDeleting(false);
        setShowConfirmDelete(false);
      });
  };

  const handleClose = () => {
    close();
  };

  return (
    <Dialog
      size="lg"
      title={`${intl.formatMessage({
        id: "quote-request.template",
        defaultMessage: "Template",
      })} (${selectedTemplate?.label})`}
      show={isOpen}
      onHide={handleClose}
    >
      <div className="view-template-container">
        {isQuickRequest ? (
          <>
            <QuickOrganisationalDetails data={data} loadingData={loadingData} />
            <QuickSchedule data={data} loadingData={loadingData} />
            <CardNew
              className={`mb-15 ${data?.organization?.additional_comments ? "" : "d-none"}`}
              isLoading={loadingData}
              title={
                <FormattedMessage id="quote-request-detail.additionalInformation" defaultMessage="Additional information" />
              }
            >
              <CardNew.Body>
                <Card.Text>{data?.organization?.additional_comments}</Card.Text>
              </CardNew.Body>
            </CardNew>
            <QuickService data={data} loadingData={loadingData} />
            <QuickPackingList data={data} loadingData={loadingData} />
          </>
        ) : (
          <>
            <QuoteOrganisationalDetails data={data} loadingData={loadingData} />
            <QuoteSchedule data={data} loadingData={loadingData} />
            <QuoteAttachments data={data} hideDownloadButton />
            <CardNew
              className={`mb-15 ${data?.organization?.additional_comments ? "" : "d-none"}`}
              isLoading={loadingData}
              title={
                <FormattedMessage id="quote-request-detail.additionalInformation" defaultMessage="Additional information" />
              }
            >
              <CardNew.Body>
                <Card.Text>{data?.organization?.additional_comments}</Card.Text>
              </CardNew.Body>
            </CardNew>

            <QuoteService data={data} loadingData={loadingData} />
            <QuotePackingList
              data={data}
              loadingData={loadingData}
              goods_confirmation_type={data?.organization?.goods_confirmation_type}
              organization_data={data?.organization}
            />
          </>
        )}
      </div>
      {!showConfirmDelete && (
        <div className="d-flex justify-content-between mt-4">
          <Button
            variant="outline-primary"
            className="mr-2"
            onClick={close}
            label={intl.formatMessage({
              id: "general.cancel",
              defaultMessage: "Cancel",
            })}
          />
          <Button
            variant="danger"
            onClick={() => setShowConfirmDelete(true)}
            label={intl.formatMessage({
              id: "shipment-quotes.components.template.deleteTemplate",
              defaultMessage: "Delete template",
            })}
          />
        </div>
      )}
      {showConfirmDelete && (
        <div className="d-flex mt-4 align-items-center justify-content-between">
          <Alert variant="warning" className="">
            <FormattedMessage
              id="shipment-quotes.components.template.deleteTemplateConfirm"
              defaultMessage="Are you sure you want to delete this template?"
            />
          </Alert>

          <span className="d-flex">
            <Button
              className="ml-3"
              variant="outline-primary"
              label={intl.formatMessage({
                id: "general.cancel",
                defaultMessage: "Cancel",
              })}
              onClick={() => setShowConfirmDelete(false)}
            />
            <Button
              className="ml-3"
              label={intl.formatMessage({
                id: "general.delete",
                defaultMessage: "Delete",
              })}
              variant="danger"
              isLoading={isDeleting}
              onClick={handleDeleteTemplate}
              data-testid={testIds.FORM_DELETE_TEMPLATE_BUTTON}
            />
          </span>
        </div>
      )}
    </Dialog>
  );
};

export default ViewTemplateDialog;
